import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { administrationApi } from '@/services/administration.api';
import { Button } from '@/components/ui';
import { InviteMemberDialog } from '../administration/invite-member-dialog';

/**
 * Whether this person has a linked TABITA user account, and a way to
 * create one when they don't — `TenantMembership.personId` is the same
 * link `/administration` already reads and writes, so this reuses its
 * dialog rather than a second invite flow.
 *
 * Membership data requires `tenancy.tenant.admin`, the same permission
 * that gates `/administration` itself, so the whole card is withheld
 * without it rather than shown empty.
 */
export function UserAccountCard({
  personId,
  personEmail,
  personDisplayName,
}: {
  readonly personId: string;
  readonly personEmail: string | null;
  readonly personDisplayName: string;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const canAdmin = can('tenancy.tenant.admin');

  const membersQuery = useQuery({
    queryKey: ['administration', 'members'],
    queryFn: () => administrationApi.listMembers(),
    enabled: canAdmin,
  });

  if (!canAdmin) return null;

  const linkedMember = membersQuery.data?.find((member) => member.personId === personId) ?? null;

  return (
    <section className="detail-section">
      <h2 className="detail-section-title">{t('people.person.userAccount.title')}</h2>

      {linkedMember ? (
        <p className="settings-status-title">
          {t('people.person.userAccount.linked', { values: { email: linkedMember.email } })}
        </p>
      ) : (
        <>
          <p className="person-card-meta">{t('people.person.userAccount.hint')}</p>
          <Button variant="secondary" onClick={() => setIsCreating(true)}>
            {t('administration.member.createAccount')}
          </Button>
        </>
      )}

      {isCreating && (
        <InviteMemberDialog
          onClose={() => setIsCreating(false)}
          onInvited={() => {
            setIsCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['administration', 'members'] });
          }}
          linkToPerson={{ personId, email: personEmail, displayName: personDisplayName }}
        />
      )}
    </section>
  );
}
